import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchLatestEventByType } from '../services/patient/timeline';
import type { AppointmentOriginEvent } from '../services/appointments';

/**
 * Live "why ODO asked for this appointment" origin event for a NEW
 * appointment draft: the in-office-hold-note timeline event, when this
 * appointment was prompted by that hold. Shaped the same as the
 * `originEvent` an EXISTING appointment carries once saved (the backend
 * snapshots it there), so both modes feed the same OdoContextCard /
 * per-event-type renderers (see Composer.tsx) instead of duplicating
 * markup.
 *
 * Only meaningful before the appointment exists - EDIT mode reads the
 * persisted snapshot instead of re-fetching live data. Disabled when
 * `patientId` is null, which the caller (Composer.tsx) also uses as the
 * Hold-2 gate - gate on internal status, not the action label; see
 * INTERNAL_STATUS_HOLD_IN_OFFICE in utils/constants.ts.
 */
export function useScheduleAppointmentContext(
    patientId: string | null
): UseQueryResult<AppointmentOriginEvent | null> {
    return useQuery({
        queryKey: ['patient', patientId, 'schedule-appointment-context'],
        queryFn: async (): Promise<AppointmentOriginEvent | null> => {
            const event = await fetchLatestEventByType(
                patientId as string,
                'in-office-hold-note'
            ).catch(() => null);
            return event ? { id: event.id, type: event.type, payload: event.payload } : null;
        },
        enabled: !!patientId,
        staleTime: 60_000
    });
}
